import React, { useEffect, useMemo, useState } from "react";
import { X, User as UserIcon, Bot, AlertCircle, Search, Undo2 } from "lucide-react";
import { toast } from "sonner";

import { eventsApi, tasksApi } from "@/lib/api";
import { fmtTime, fmtDate } from "@/lib/format";

const ICONS = {
  user: <UserIcon className="w-3 h-3 text-cyan" />,
  ai: <Bot className="w-3 h-3 text-amber" />,
  system: <AlertCircle className="w-3 h-3 text-ink-mute" />,
};

const ACTION_LABELS = {
  task_created: "Task created",
  task_deleted: "Task deleted",
  task_restored: "Task restored",
  status_changed: "Status changed",
  locked: "Locked",
  unlocked: "Unlocked",
  block_created: "Block scheduled",
  block_moved: "Block moved",
  block_resized: "Block resized",
  block_deleted: "Block deleted",
  blocker_added: "Blocker added",
  blocker_resolved: "Blocker resolved",
  ai_adjusted: "AI adjusted",
  recurrence_materialized: "Recurrence materialised",
  subtasks_rolled_over: "Subtasks rolled over",
  equipment_allocated: "Equipment allocated",
  equipment_deallocated: "Equipment de-allocated",
  member_invited: "Member invited",
  member_removed: "Member removed",
  project_duplicated: "Project duplicated",
  project_deleted: "Project deleted",
  daily_wrap_locked: "Daily wrap locked",
};

/**
 * Project-wide audit log. Lists every entry from `schedule_events` for the
 * active project, newest first. Filter by free-text or action type; collapses
 * timestamps into "Today · 14:30" / "Yesterday · 09:00" / "Tue 19 May · 11:00".
 */
export default function AuditLogDrawer({ open, onClose, projectId, taskById, blockById, userById, onRestored }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState("");
  const [actionFilter, setActionFilter] = useState("all");
  // Track tasks that were just restored in-session so we can grey the button
  // (server-side it's a no-op if we click again, but the visual state matters).
  const [restoredIds, setRestoredIds] = useState(() => new Set());

  const loadEvents = () => {
    setLoading(true);
    eventsApi
      .forProject(projectId, 300)
      .then(setEvents)
      .catch((e) => toast.error(e.response?.data?.detail || "Failed to load audit log"))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (!open || !projectId) return;
    loadEvents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, projectId]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  const actionTypes = useMemo(() => {
    const set = new Set(events.map((e) => e.action).filter(Boolean));
    return ["all", ...[...set].sort()];
  }, [events]);

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return events.filter((e) => {
      if (actionFilter !== "all" && e.action !== actionFilter) return false;
      if (!q) return true;
      const haystack = [
        e.action || "",
        e.actor_type || "",
        taskById?.[e.task_id]?.title || "",
        JSON.stringify(e.payload || {}),
      ].join(" ").toLowerCase();
      return haystack.includes(q);
    });
  }, [events, filter, actionFilter, taskById]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[85] flex bg-black/70 backdrop-blur-sm"
      onClick={onClose}
      data-testid="audit-overlay"
    >
      <div className="flex-1" />
      <div
        className="w-[640px] max-w-full h-full bg-bg-surface border-l border-line-strong shadow-[0_0_64px_rgba(0,0,0,0.7)] animate-slide-up flex flex-col"
        onClick={(e) => e.stopPropagation()}
        data-testid="audit-drawer"
      >
        <header className="flex items-center justify-between p-3 border-b border-line-strong bg-bg-elevated">
          <div>
            <div className="mono text-2xs uppercase tracking-[0.2em] text-ink-mute">AUDIT LOG</div>
            <div className="text-sm text-ink mt-0.5">
              {loading ? "loading…" : `${filtered.length} of ${events.length} event${events.length === 1 ? "" : "s"}`}
            </div>
          </div>
          <button onClick={onClose} className="btn-ghost" data-testid="audit-close">
            <X className="w-3 h-3" />
          </button>
        </header>

        <div className="px-3 py-2 border-b border-line bg-bg-base flex items-center gap-2">
          <Search className="w-3 h-3 text-ink-mute shrink-0" />
          <input
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="filter by task, actor, action…"
            className="flex-1 bg-bg-elevated text-sm p-1.5 outline-none border border-line focus:border-amber"
            data-testid="audit-filter-input"
          />
          <select
            value={actionFilter}
            onChange={(e) => setActionFilter(e.target.value)}
            className="mono text-2xs uppercase tracking-widest bg-bg-elevated text-ink p-1.5 outline-none border border-line focus:border-amber"
            data-testid="audit-action-filter"
          >
            {actionTypes.map((a) => (
              <option key={a} value={a}>
                {a === "all" ? "ALL ACTIONS" : (ACTION_LABELS[a] || a).toUpperCase()}
              </option>
            ))}
          </select>
        </div>

        <div className="flex-1 overflow-auto p-3 space-y-1">
          {loading ? (
            <div className="mono text-2xs uppercase tracking-widest text-ink-mute text-center py-12">
              loading…
            </div>
          ) : filtered.length === 0 ? (
            <div className="mono text-2xs uppercase tracking-widest text-ink-mute text-center py-12">
              no events
            </div>
          ) : (
            filtered.map((e) => (
              <EventRow
                key={e.id}
                ev={e}
                taskTitle={taskById?.[e.task_id]?.title}
                blockTitle={blockById?.[e.schedule_block_id]?.start_at}
                actorName={userById?.[e.actor_user_id]?.name || e.actor_user_id}
                isRestored={restoredIds.has(e.task_id)}
                onRestore={async () => {
                  try {
                    const res = await tasksApi.restore(e.task_id);
                    if (res?.restored === 0) {
                      toast.message("Already active");
                    } else {
                      toast.success(`Restored ${res?.restored || 1} item${(res?.restored || 1) === 1 ? "" : "s"}`);
                    }
                    setRestoredIds((s) => new Set(s).add(e.task_id));
                    onRestored && onRestored();
                    loadEvents();
                  } catch (err) {
                    toast.error(err.response?.data?.detail || "Restore failed");
                  }
                }}
              />
            ))
          )}
        </div>
      </div>
    </div>
  );
}

function EventRow({ ev, taskTitle, actorName, isRestored, onRestore }) {
  const icon = ICONS[ev.actor_type] || ICONS.system;
  const label = ACTION_LABELS[ev.action] || ev.action;
  const ts = ev.created_at;
  const detail = describePayload(ev);
  const canRestore = ev.action === "task_deleted" && ev.task_id && onRestore;

  return (
    <div
      className="flex gap-3 px-2 py-1.5 border border-transparent hover:border-line hover:bg-bg-elevated transition-colors group"
      data-testid={"audit-event-" + ev.id}
    >
      <div className="mt-0.5 shrink-0">{icon}</div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 text-xs">
          <span className="mono text-2xs uppercase tracking-widest text-amber shrink-0">{label}</span>
          {taskTitle && (
            <span className="text-ink truncate" title={taskTitle}>
              {taskTitle}
            </span>
          )}
        </div>
        {detail && (
          <div className="text-2xs text-ink-dim leading-relaxed mt-0.5">{detail}</div>
        )}
        <div className="mono text-2xs text-ink-mute uppercase tracking-widest mt-0.5">
          {fmtRelative(ts)} · {actorName || "—"}
        </div>
      </div>
      {canRestore && (
        <button
          onClick={onRestore}
          disabled={isRestored}
          className={
            "shrink-0 self-start mono text-2xs uppercase tracking-widest px-2 py-1 border transition-colors flex items-center gap-1 " +
            (isRestored
              ? "border-line text-ink-mute opacity-60 cursor-default"
              : "border-amber text-amber hover:bg-amber-glow")
          }
          data-testid={"audit-restore-" + ev.task_id}
          title={isRestored ? "Already restored in this session" : "Undo the delete — restores the task and its blocks/subtasks"}
        >
          <Undo2 className="w-3 h-3" />
          {isRestored ? "RESTORED" : "RESTORE"}
        </button>
      )}
    </div>
  );
}

function describePayload(ev) {
  const p = ev.payload || {};
  switch (ev.action) {
    case "status_changed":
      return p.from && p.to ? `${p.from} → ${p.to}` : null;
    case "block_moved":
      return p.from && p.to ? `${fmtTime(p.from)} → ${fmtTime(p.to)}` : null;
    case "block_resized":
      return p.delta_min ? `${p.delta_min > 0 ? "+" : ""}${p.delta_min}m` : null;
    case "member_invited":
      return p.email ? `${p.email} as ${p.role || "member"}` : null;
    case "recurrence_materialized":
      return p.created != null ? `created ${p.created} · skipped ${p.skipped || 0}` : null;
    case "subtasks_rolled_over":
      return p.count ? `${p.count} subtask${p.count === 1 ? "" : "s"} → next` : null;
    case "equipment_allocated":
      return p.quantity ? `${p.quantity}× equipment` : null;
    case "daily_wrap_locked":
      return p.date_key || null;
    default:
      return null;
  }
}

function fmtRelative(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    const now = new Date();
    const sameDay = d.toDateString() === now.toDateString();
    if (sameDay) return "Today · " + fmtTime(iso);
    const yest = new Date(now); yest.setDate(now.getDate() - 1);
    if (d.toDateString() === yest.toDateString()) return "Yesterday · " + fmtTime(iso);
    return fmtDate(iso) + " · " + fmtTime(iso);
  } catch {
    return iso;
  }
}

// keep relative imports tidy
